import React, { useState, useEffect } from 'react';
import { AiOutlineLike, AiOutlineDislike, AiFillLike, AiFillDislike } from 'react-icons/ai';
import { submitSectionFeedback, getSectionFeedback } from '../context/engine.ts';
import { supabase } from '../integrations/client.ts';

/**
 * SectionFeedback Component
 * Displays thumbs up/down feedback buttons after user passes a quiz
 * Only shown if user hasn't submitted feedback yet
 */
export default function SectionFeedback({ sectionId, onFeedbackSubmitted }) {
    const [userId, setUserId] = useState(null);
    const [selectedRating, setSelectedRating] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [error, setError] = useState(null);

    // Get current user
    useEffect(() => {
        const getUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                setUserId(user.id);
                // Check if feedback already exists
                const feedback = await getSectionFeedback(user.id, sectionId);
                if (feedback.rating) {
                    setSelectedRating(feedback.rating);
                    setHasSubmitted(true);
                }
            }
        };
        getUser();
    }, [sectionId]);

    const handleFeedbackClick = async (rating) => {
        if (!userId || isSubmitting || hasSubmitted) return;

        setIsSubmitting(true);
        setError(null);

        try {
            await submitSectionFeedback(userId, sectionId, rating);
            setSelectedRating(rating);
            setHasSubmitted(true);

            // Notify parent component
            if (onFeedbackSubmitted) {
                onFeedbackSubmitted(rating);
            }
        } catch (err) {
            console.error('Failed to submit feedback:', err);
            setError('Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Don't render if user not loaded
    if (!userId) return null;

    return (
        <div className="section-feedback-container">
            {!hasSubmitted ? (
                <>
                    <div className="feedback-question">
                        How was this section?
                    </div>
                    <div className="feedback-buttons">
                        <button
                            onClick={() => handleFeedbackClick('positive')}
                            disabled={isSubmitting}
                            className="feedback-btn thumbs-up"
                            aria-label="Thumbs up"
                        >
                            <AiOutlineLike size={28} />
                        </button>
                        <button
                            onClick={() => handleFeedbackClick('negative')}
                            disabled={isSubmitting}
                            className="feedback-btn thumbs-down"
                            aria-label="Thumbs down"
                        >
                            <AiOutlineDislike size={28} />
                        </button>
                    </div>
                    {error && <div className="feedback-error">{error}</div>}
                </>
            ) : (
                <div className="feedback-thank-you">
                    <div className="feedback-icon">
                        {selectedRating === 'positive' ? (
                            <AiFillLike size={24} />
                        ) : (
                            <AiFillDislike size={24} />
                        )}
                    </div>
                    <span>Thank you for your feedback!</span>
                </div>
            )}

            <style jsx>{`
                .section-feedback-container {
                    padding: 20px 0;
                    border-top: 1px solid #e5e5e5;
                    margin-top: 20px;
                    text-align: center;
                }

                .feedback-question {
                    font-size: 15px;
                    font-weight: 500;
                    color: #333333;
                    margin-bottom: 15px;
                }

                .feedback-buttons {
                    display: flex;
                    justify-content: center;
                    gap: 20px;
                }

                .feedback-btn {
                    background: transparent;
                    border: 2px solid #333333;
                    border-radius: 50%;
                    width: 56px;
                    height: 56px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #333333;
                    padding: 0;
                }

                .feedback-btn:hover:not(:disabled) {
                    background: #f5f5f5;
                    transform: scale(1.1);
                }

                .feedback-btn:active:not(:disabled) {
                    transform: scale(0.95);
                }

                .feedback-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .feedback-thank-you {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    font-size: 15px;
                    color: #333333;
                    font-weight: 500;
                }

                .feedback-icon {
                    display: flex;
                    align-items: center;
                    color: #333333;
                }

                .feedback-error {
                    margin-top: 10px;
                    color: #d32f2f;
                    font-size: 13px;
                }
            `}</style>
        </div>
    );
}
